import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon, Markdown, MarkdownToolbar, handleToolbarKeydown } from '@carrent/ui'
import { kbApi } from '@carrent/api'

const AUTOSAVE_MS = 1200

const resolveImage = (href) => {
  const m = String(href || '').match(/^attachment:(\d+)$/)
  if (m) return kbApi.attachmentUrl(m[1])
  return /^(https?:|\/)/i.test(href) ? href : null
}

/**
 * Правка статьи.
 *
 * Слева исходник, справа то, что увидит читатель. Предпросмотр рядом,
 * а не по кнопке: регламент с таблицей набирают, глядя на результат.
 *
 * Черновик сохраняется сам, с задержкой. Автор с уровнем «правка»
 * отправляет его на проверку, админ пространства публикует сразу —
 * кнопка называется тем, что произойдёт, а не «Сохранить».
 */
export function ArticleEditor({ page, level, onDone, onCancel }) {
  const initial = page.my_revision || {}
  const [title, setTitle] = useState(initial.title || page.title || '')
  const [body, setBody] = useState(initial.body_md ?? page.body_md ?? '')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const timer = useRef(null)
  const dirty = useRef(false)
  const bodyRef = useRef(null)

  const canPublish = level === 'admin'
  const pending = initial.status === 'pending'
  const rejected = initial.status === 'rejected'

  const save = useCallback(async () => {
    if (!dirty.current) return true
    setSaving(true)
    setError('')
    try {
      await kbApi.updatePage(page.id, { title, body_md: body })
      dirty.current = false
      setSavedAt(new Date())
      return true
    } catch (e) {
      setError(e.response?.data?.message || 'Черновик не сохранился')
      return false
    } finally {
      setSaving(false)
    }
  }, [page.id, title, body])

  // Автосохранение с задержкой: писать на каждое нажатие незачем.
  useEffect(() => {
    if (!dirty.current || pending) return undefined
    clearTimeout(timer.current)
    timer.current = setTimeout(save, AUTOSAVE_MS)
    return () => clearTimeout(timer.current)
  }, [title, body, save, pending])

  const change = (setter) => (value) => {
    dirty.current = true
    setter(value)
  }

  const finish = async (action) => {
    setBusy(true)
    const ok = await save()
    if (!ok) {
      setBusy(false)
      return
    }
    try {
      if (action === 'publish') await kbApi.publish(page.id)
      else await kbApi.submit(page.id)
      onDone()
    } catch (e) {
      setError(e.response?.data?.message || 'Не получилось')
      setBusy(false)
    }
  }

  const attach = async (file) => {
    if (!file) return
    const form = new FormData()
    form.append('file', file)
    try {
      const r = await kbApi.upload(page.id, form)
      const att = r.data?.data
      const snippet = `\n![${att.filename}](attachment:${att.id})\n`
      dirty.current = true
      setBody((b) => b + snippet)
    } catch (e) {
      setError(e.response?.data?.message || 'Файл не загрузился')
    }
  }

  if (pending) {
    return (
      <div className="kb-state kb-state--inline">
        <Icon name="warning" size={28} aria-hidden="true" />
        <p className="kb-state__title">Правка уже на проверке</p>
        <p className="kb-state__hint">
          Продолжить редактирование можно после решения проверяющего.
        </p>
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          Вернуться к статье
        </button>
      </div>
    )
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <input
          className="editor__title"
          value={title}
          aria-label="Заголовок статьи"
          placeholder="Заголовок статьи"
          onChange={(e) => change(setTitle)(e.target.value)}
        />
        <div className="editor__status" aria-live="polite">
          {saving ? 'Сохраняем…' : savedAt ? 'Черновик сохранён' : ''}
        </div>
      </header>

      {rejected && initial.review_comment && (
        <div className="editor__rejected">
          <Icon name="warning" size={16} aria-hidden="true" />
          <div>
            <strong>Правку вернули на доработку.</strong>
            <p>{initial.review_comment}</p>
          </div>
        </div>
      )}

      <div className="editor__panes">
        <div className="editor__pane">
          <MarkdownToolbar
            textareaRef={bodyRef}
            onChange={change(setBody)}
            extra={
              <label className="btn btn--ghost btn--sm editor__attach">
                <Icon name="camera" size={15} aria-hidden="true" />
                Вложить файл
                <input
                  type="file"
                  className="editor__file"
                  accept="image/*,application/pdf"
                  onChange={(e) => { attach(e.target.files?.[0]); e.target.value = '' }}
                />
              </label>
            }
          />
          <textarea
            ref={bodyRef}
            className="editor__body"
            value={body}
            aria-label="Текст статьи"
            spellCheck="true"
            placeholder={'# Заголовок раздела\n\nТекст регламента. **Жирный**, списки, таблицы.'}
            onChange={(e) => change(setBody)(e.target.value)}
            onKeyDown={(e) => handleToolbarKeydown(e, bodyRef, change(setBody))}
          />
        </div>

        <div className="editor__pane editor__pane--preview">
          <div className="editor__toolbar">
            <span className="editor__pane-label">Как увидит читатель</span>
          </div>
          <div className="editor__preview">
            <Markdown
              text={body}
              resolveImage={resolveImage}
              onTasksChange={change(setBody)}
            />
          </div>
        </div>
      </div>

      {error && <p className="field__error editor__error">{error}</p>}

      <footer className="editor__foot">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          Закрыть
        </button>
        <span className="editor__spacer" />
        {canPublish ? (
          <button type="button" className="btn btn--primary" disabled={busy}
            onClick={() => finish('publish')}>
            Опубликовать
          </button>
        ) : (
          <button type="button" className="btn btn--primary" disabled={busy}
            onClick={() => finish('submit')}>
            Отправить на проверку
          </button>
        )}
      </footer>
    </div>
  )
}
